import {buildSemanticTokens} from '../buildSemanticTokens';
import type {DesignSystemDefinition} from '../types';

const neutral = {
  'white-50': 'rgba(255, 255, 255, 0.5)',
  'primary-black': 'black',
  900: '#0c0407',
  800: '#333',
  700: '#4c4c4c',
  600: '#666',
  500: 'grey',
  400: '#999',
  300: '#b3b3b3',
  200: '#ccc',
  100: '#e5e5e5',
  'black-50': '#f9f9f9',
  transparent: 'rgba(255, 255, 255, 0)',
};

const greenColors = {
  2: '#071112',
  3: '#D0DDDD',
  4: '#DEE7E7',
  5: '#EDF2F2',
  30: '#5FE885',
  40: '#B2D0D4',
  50: '#E5FBF2',
  70: '#7FAFB5',
  80: '#3F585B',
  100: '#3C7077',
  200: '#174146',
  500: '#24555C',
  600: '#1D4449',
  700: '#163438',
  800: '#404040',
  850: '#757575',
  900: '#0E2225',
};

const orangeColors = {
  3: '#DDD5D0',
  4: '#E7E2DE',
  5: '#F2EFED',
  6: '#FDFCFC',
  7: '#F5F5F5',
  40: '#EDBCA1',
  70: '#E88B58',
  100: '#E06B2B',
  500: '#FF640F',
  800: '#D9D9D9',
  900: '#D94D00',
  950: '#AF4206',
};

const primary = {
  30: '#D9D9D9',
  50: '#a7a7a7',
  70: '#757575',
  100: '#565656',
  200: '#404040',
};

const semantic = buildSemanticTokens({neutral, greenColors, orangeColors});

export const designSystem20260615: DesignSystemDefinition = {
  version: '20260615',
  neutral,
  greenColors,
  orangeColors,
  primary,
  warningColor: '#B42318',
  warningColorLight: '#FDA29B',
  warningColorDark: '#912018',
  actions: {
    cta: orangeColors[900],
    ctaHover: greenColors[700],
    ctaDisabled: orangeColors[40],
    primary: greenColors[700],
    primaryHover: greenColors[100],
    destructive: '#B42318',
    destructiveHover: '#912018',
  },
  accent: {
    main: greenColors[500],
    subtle: greenColors[5],
    icon: greenColors[30],
  },
  text: {
    primary: greenColors[700],
    secondary: greenColors[700],
  },
  empionOrangeGradient: {
    base: 'linear-gradient(180deg, #FFDEB3 0%,#F5F5F5 68.75%, #FFFAF5 100%)',
    medium:
      'linear-gradient(180deg, #0000 44%, white), radial-gradient(circle farthest-corner at 100%' +
      ' 0%, #da510680 7%,' +
      ' #fff0 73%), radial-gradient(circle farthest-corner at 0% 0%, #ffdda7, #fff8ec',
    dark:
      'radial-gradient(circle farthest-corner at 0% 50%, #ffdda7, #fff0 62%),' +
      ' radial-gradient(circle farthest-side' +
      ' at 100% 0%, #ffc36f, #fff0 38%)',
    navbar:
      'linear-gradient(180deg, #0000 44%, white), radial-gradient(circle farthest-corner at 100%' +
      ' 0%, #da510680 7%,' +
      ' #fff0 73%), radial-gradient(circle farthest-corner at 0% 0%, #ffdda7, #fff8ec',
  },
  scrollbar: {
    track: greenColors[4],
    thumb: greenColors[70],
    thumbHover: orangeColors[40],
  },
  lightModePalette: {
    primary: {
      main: greenColors[500],
      contrastText: greenColors[900],
    },
    secondary: {
      main: orangeColors[100],
      light: orangeColors[500],
      dark: orangeColors[900],
    },
    background: {
      default: orangeColors[5],
      paper: '#fff',
      menu: '#fff',
      dark: orangeColors[3],
    },
    text: {
      primary: greenColors[700],
      secondary: greenColors[700],
    },
    info: {
      main: greenColors[500],
      contrastText: greenColors[900],
    },
    warning: {
      main: orangeColors[100],
      contrastText: greenColors[900],
    },
  },
  ...semantic,
};
